import { useContext, useMemo } from 'react';
import _ from 'lodash';
import cx from 'classnames';
import { Button } from 'reactstrap';

import { PanelSection } from '../SidePanel';
import SectionsIcon from '../icons/SectionsIcon';
import { AREA_UNITS } from '../../constants/units';
import { useUnits } from '../../contexts/UnitContext';
import { MapContext } from '../../contexts/MapContext';
import { useStartYear } from '../../utils/hooks';
import { asDynamicDecimal } from '../../utils/formatters';
import { useToggleSectionVisibility } from './utils/visibility';
import { alphanumericSort } from '../../utils/utils';
import { PANEL_INNER_HEIGHT } from './constants';
import { FieldsPreviewContext, SectionHeader } from './components';
import { useFarmMeta } from './utils/content';

const ROW_HEIGHT = 18;
const ROW_MARGIN = 4;

const Separator = () => <div className="mx-1">|</div>;

const Row = ({
  section: { id, name, startYear, shown },
  sectionData,
  farmStartYear,
  transformArea,
  areaUnitName,
  toggle,
}) => {
  const acreage = sectionData[id]?.acreage;
  const formattedArea =
    acreage && `${asDynamicDecimal(transformArea(acreage))} ${areaUnitName}`;

  return (
    <Button
      key={id}
      className={cx('p-0 w-100', { 'text-muted': !shown })}
      color="text"
      size="sm"
      style={{ height: `${ROW_HEIGHT}px`, marginBottom: `${ROW_MARGIN}px` }}
      onClick={toggle}
    >
      <div className="d-flex align-items-center w-100">
        <div className="text-truncate min-w-0">{name}</div>
        <div className="d-flex flex-shrink-0">
          <Separator />
          {startYear ?? farmStartYear}
          {formattedArea && (
            <>
              <Separator />
              {formattedArea}
            </>
          )}
        </div>
      </div>
    </Button>
  );
};

export const useSectionsInfo = () => {
  const { isPrintMode, panelVisibility, togglePanelVisibility } = useContext(
    FieldsPreviewContext,
  );

  const {
    transformToUnit: transformArea,
    displayUnit: areaUnitName,
  } = useUnits(AREA_UNITS);

  const farmStartYear = useStartYear();
  const { sectionData } = useFarmMeta();

  const {
    fields: { fieldSections },
  } = useContext(MapContext);

  const shownSections = useMemo(() => fieldSections.filter((s) => s.shown), [
    fieldSections,
  ]);

  const filteredSections = useMemo(
    () => (isPrintMode ? shownSections : fieldSections),
    [fieldSections, shownSections, isPrintMode],
  );

  const sortedFieldSections = useMemo(
    () =>
      [...filteredSections].sort((a, b) => alphanumericSort(a.name, b.name)),
    [filteredSections],
  );

  const rowsPerPanel = Math.floor(
    PANEL_INNER_HEIGHT / (ROW_HEIGHT + ROW_MARGIN),
  );

  const chunks = _.chunk(sortedFieldSections, rowsPerPanel);

  const toggleSectionVisibility = useToggleSectionVisibility();

  const header = (
    <SectionHeader
      title="Sections"
      icon={<SectionsIcon />}
      onClick={() => togglePanelVisibility('sections')}
    />
  );

  if (!panelVisibility.sections) {
    if (isPrintMode) return [];

    return [
      <PanelSection className="opacity-50" key="sections-hidden">
        {header}
      </PanelSection>,
    ];
  }

  return chunks.map((chunk, i) => (
    <PanelSection
      $hideBottomBorder={i !== chunks.length - 1}
      key={chunk.map(({ id }) => id).join('-')}
    >
      {i === 0 && header}

      <div className="pr-2">
        {chunk.map((section) => (
          <Row
            key={section.id}
            section={section}
            sectionData={sectionData}
            farmStartYear={farmStartYear}
            transformArea={transformArea}
            areaUnitName={areaUnitName}
            toggle={() => toggleSectionVisibility(section.id)}
          />
        ))}
      </div>
    </PanelSection>
  ));
};
